import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IoImageOutline, IoClose, IoGlobeOutline, IoPeopleOutline, IoLockClosedOutline, IoChevronDown } from 'react-icons/io5';
import { FaMicrophone } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { postApi } from '../services/postApi';
import LinkPreview from './LinkPreview';
import AudioComposer from './AudioComposer';
import MentionInput from './MentionInput';
import './PostForm.css';

const PostForm = ({ onPostCreated }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceBlobUrl, setVoiceBlobUrl] = useState(null);
  const [visibility, setVisibility] = useState(() => {
    if (!user) return 'public';
    return localStorage.getItem(`postVisibility_${user.id}`) || 'public';
  });
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  const visibilityRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const fetchTimeoutRef = useRef(null);

  const visibilityOptions = [
    { value: 'public', icon: IoGlobeOutline, label: t('post.visibilityPublic') || 'Public' },
    { value: 'friends', icon: IoPeopleOutline, label: t('post.visibilityFriends') || 'Friends' },
    { value: 'only_me', icon: IoLockClosedOutline, label: t('post.visibilityOnlyMe') || 'Only me' },
  ];

  const currentVisibility = visibilityOptions.find(o => o.value === visibility) || visibilityOptions[1];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (visibilityRef.current && !visibilityRef.current.contains(e.target)) {
        setShowVisibilityMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVisibilityChange = (value) => {
    setVisibility(value);
    if (user) {
      localStorage.setItem(`postVisibility_${user.id}`, value);
    }
    setShowVisibilityMenu(false);
  };

  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  // Extract URLs from text (with or without http/https)
  const extractURL = useCallback((text) => {
    // First try to match URLs with protocol
    const urlWithProtocol = /(https?:\/\/[^\s]+)/g;
    let matches = text.match(urlWithProtocol);
    if (matches) return matches[0];

    // Then try to match URLs without protocol (e.g., example.com, www.example.com)
    const urlWithoutProtocol = /(?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?/g;
    matches = text.match(urlWithoutProtocol);
    if (matches) {
      // Prepend https:// if no protocol
      const url = matches[0];
      return url.startsWith('www.') || url.startsWith('http') ? url : 'https://' + url;
    }

    return null;
  }, []);

  // Fetch link preview
  const fetchLinkPreview = useCallback(async (url) => {
    if (!url) {
      setLinkPreview(null);
      return;
    }

    setIsLoadingPreview(true);
    try {
      const response = await postApi.fetchLinkPreview(url);
      if (response.data) {
        setLinkPreview(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch link preview:', error);
      // Silently fail - link preview is optional
    } finally {
      setIsLoadingPreview(false);
    }
  }, []);

  // Detect URLs in content and fetch preview (only when no media files attached)
  useEffect(() => {
    // Clear previous timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Don't show link preview if there are media files attached
    if (files.length > 0) {
      if (linkPreview) {
        setLinkPreview(null);
      }
      return;
    }

    // Extract URL from content
    const url = extractURL(content);

    if (url && url !== linkPreview?.url) {
      // Debounce fetching to avoid too many requests
      fetchTimeoutRef.current = setTimeout(() => {
        fetchLinkPreview(url);
      }, 500);
    } else if (!url && linkPreview) {
      // Clear preview if URL was removed
      setLinkPreview(null);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [content, files.length, linkPreview, extractURL, fetchLinkPreview]);

  // Warn user before leaving page with unsaved content
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (content.trim() || files.length > 0 || linkPreview || voiceBlob) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [content, files.length, linkPreview, voiceBlob]);

  const handleContentChange = (e) => {
    setContent(e.target.value);
    // Only call autoResizeTextarea if we have a real DOM element
    if (e.target && typeof e.target.getBoundingClientRect === 'function') {
      autoResizeTextarea(e.target);
    } else if (textareaRef?.current) {
      // Fallback to using the ref if target is a synthetic event
      autoResizeTextarea(textareaRef.current);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = [...files, ...selectedFiles].slice(0, 4); // Max 4 files
    setFiles(newFiles);

    // Generate previews
    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => {
          const newPreviews = [...prev, { url: reader.result, type: file.type }];
          return newPreviews.slice(0, 4);
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    // Reset file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartVoiceRecording = () => {
    setIsRecordingVoice(true);
  };

  const handleRecordingComplete = (blob) => {
    setVoiceBlob(blob);
    setVoiceBlobUrl(URL.createObjectURL(blob));
    setIsRecordingVoice(false);
    setError(''); // Clear any previous error
  };

  const handleCancelRecording = () => {
    setIsRecordingVoice(false);
    if (voiceBlobUrl) URL.revokeObjectURL(voiceBlobUrl);
    setVoiceBlob(null);
    setVoiceBlobUrl(null);
  };

  const handleRemoveVoice = () => {
    if (voiceBlobUrl) URL.revokeObjectURL(voiceBlobUrl);
    setVoiceBlob(null);
    setVoiceBlobUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() && files.length === 0 && !voiceBlob) {
      setError(t('post.contentRequired') || 'Please add some content, media, or voice');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Create post with text content, visibility, and link preview
      const postData = { content: content.trim(), visibility };
      if (linkPreview && files.length === 0 && !voiceBlob) {
        postData.link_preview = linkPreview;
      }
      const response = await postApi.createPost(postData);
      const post = response.data;

      // Upload media files if any
      if (files.length > 0) {
        for (const file of files) {
          await postApi.uploadPostMedia(post.id, file);
        }
      }

      // Upload voice if any
      if (voiceBlob) {
        await postApi.uploadPostVoice(post.id, voiceBlob);
      }

      // Clear form
      setContent('');
      setFiles([]);
      setPreviews([]);
      setLinkPreview(null);
      setVoiceBlob(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      // Notify parent
      if (onPostCreated) {
        onPostCreated();
      }
    } catch (err) {
      setError(err.response?.data?.error || t('post.createError') || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="post-form-container">
      <form onSubmit={handleSubmit} className="post-form">
        <MentionInput
          value={content}
          onChange={handleContentChange}
          placeholder={t('post.placeholder') || "What's on your mind?"}
          className="post-textarea"
          rows={1}
          maxLength={5000}
          inputRef={textareaRef}
        />

        {previews.length > 0 && (
          <div className="post-previews">
            {previews.map((preview, index) => (
              <div key={index} className="preview-item">
                {preview.type.startsWith('image/') ? (
                  <img src={preview.url} alt={`Preview ${index + 1}`} />
                ) : (
                  <video src={preview.url} controls />
                )}
                <button
                  type="button"
                  className="remove-preview"
                  onClick={() => removeFile(index)}
                  aria-label="Remove image"
                >
                  <IoClose />
                </button>
              </div>
            ))}
          </div>
        )}

        {isLoadingPreview && (
          <div className="link-preview-loading">Loading preview...</div>
        )}

        {linkPreview && !isLoadingPreview && (
          <LinkPreview
            preview={linkPreview}
            onRemove={() => setLinkPreview(null)}
          />
        )}

        {(isRecordingVoice || voiceBlob) && (
          <AudioComposer
            voiceBlob={voiceBlob}
            voiceBlobUrl={voiceBlobUrl}
            onRecordingComplete={handleRecordingComplete}
            onCancel={handleCancelRecording}
            onRemove={handleRemoveVoice}
            maxDuration={30}
          />
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="post-form-actions">
          <div className="post-form-actions-left">
            <div className="visibility-selector" ref={visibilityRef}>
              <button
                type="button"
                className="visibility-toggle"
                onClick={() => setShowVisibilityMenu(!showVisibilityMenu)}
              >
                <currentVisibility.icon />
                <span>{currentVisibility.label}</span>
                <IoChevronDown className="visibility-chevron" />
              </button>
              {showVisibilityMenu && (
                <div className="visibility-menu">
                  {visibilityOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`visibility-option ${option.value === visibility ? 'active' : ''}`}
                      onClick={() => handleVisibilityChange(option.value)}
                    >
                      <option.icon />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="media-upload"
              disabled={voiceBlob !== null}
            />
            <label
              htmlFor="media-upload"
              className={`icon-button ${voiceBlob ? 'disabled' : ''}`}
              data-tooltip={voiceBlob ? 'Remove voice to add media' : (t('post.addMedia') || 'Add photo or video')}
            >
              <IoImageOutline />
            </label>

            <button
              type="button"
              className={`icon-button ${files.length > 0 ? 'disabled' : ''}`}
              onClick={handleStartVoiceRecording}
              disabled={files.length > 0 || voiceBlob !== null || isRecordingVoice}
              data-tooltip={files.length > 0 ? 'Remove media to add voice' : (t('post.addVoice') || 'Add voice message')}
            >
              <FaMicrophone />
            </button>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || (!content.trim() && files.length === 0 && !voiceBlob)}
          >
            {isSubmitting ? t('post.posting') || 'Posting...' : t('post.post') || 'Post'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostForm;
